/**
 * Dashboard-specific hooks using TanStack Query
 * Thin wrappers around existing API services for dashboard data
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ChangesFeedService } from '@/api/services/ChangesFeedService';
import { EventsService } from '@/api/services/EventsService';
import { ActivitiesService } from '@/api/services/ActivitiesService';
import { InvoicesService } from '@/api/services/InvoicesService';
import { retryUnlessClientError } from '@/lib/queryRetry';
import { invoiceKeys } from '@/hooks/useInvoices';
import type { changes_ChangesFeedSummaryResponse } from '@/api/models/changes_ChangesFeedSummaryResponse';
import type { models_CalendarEvent } from '@/api/models/models_CalendarEvent';
import type { activities_ListActivitiesResponse } from '@/api/models/activities_ListActivitiesResponse';
import { NAV_BADGE_POLL_MS, visiblePoll } from '@/lib/polling';

type OrgKey = string | null | undefined;

export const dashboardKeys = {
  all: ['dashboard'] as const,
  changesFeedSummary: (orgId: OrgKey, lookaheadDays: number) =>
    [...dashboardKeys.all, orgId ?? null, 'changes-feed-summary', { lookaheadDays }] as const,
  upcomingEvents: (orgId: OrgKey, from: string, to: string) =>
    [...dashboardKeys.all, orgId ?? null, 'upcoming-events', { from, to }] as const,
  todayActivities: (orgId: OrgKey, from: string, to: string) =>
    [...dashboardKeys.all, orgId ?? null, 'today-activities', { from, to }] as const,
};

/**
 * Hook to fetch changes feed summary (arrivals/departures per group).
 */
export function useChangesFeedSummary(
  lookaheadDays = 30,
  options?: { enabled?: boolean },
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: dashboardKeys.changesFeedSummary(orgId, lookaheadDays),
    queryFn: () =>
      ChangesFeedService.getApiV1ChangesFeedSummary(
        undefined,
        lookaheadDays,
      ) as Promise<changes_ChangesFeedSummaryResponse>,
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/** The events of `GET /events`: `{ events: [...] }` on the wire, a list in the generated type. */
export function toEventList(response: unknown): models_CalendarEvent[] {
  if (Array.isArray(response)) return response as models_CalendarEvent[];
  return (response as { events?: models_CalendarEvent[] } | undefined)?.events ?? [];
}

/**
 * Hook to fetch upcoming calendar events.
 * The API returns { events: [...], total: N } but the generated type says Array.
 * We normalize to always return an array.
 */
export function useUpcomingEvents(
  from: string,
  to: string,
  options?: { enabled?: boolean },
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: dashboardKeys.upcomingEvents(orgId, from, to),
    queryFn: async () => {
      return toEventList(await EventsService.getApiV1Events(from, to));
    },
    enabled: (options?.enabled ?? true) && !!from && !!to && isSignedIn && isAuthReady,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to fetch today's activity count.
 * Expects dateFrom and dateTo in RFC3339 format.
 */
export function useTodayActivityCount(
  dateFrom: string,
  dateTo?: string,
  options?: { enabled?: boolean },
) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();
  const to = dateTo ?? dateFrom;

  return useQuery({
    queryKey: dashboardKeys.todayActivities(orgId, dateFrom, to),
    queryFn: () =>
      ActivitiesService.getApiV1Activities(
        1,
        1,
        undefined,
        undefined,
        dateFrom,
        to,
      ) as Promise<activities_ListActivitiesResponse>,
    enabled: (options?.enabled ?? true) && !!dateFrom && isSignedIn && isAuthReady,
    staleTime: 2 * 60 * 1000,
    retry: retryUnlessClientError,
  });
}

/**
 * Hook to count overdue invoices (`status=OVERDUE`, `limit=1`, `total_count`)
 * for the nav badge. Keyed under the invoice lists so every invoice mutation
 * that refreshes the lists refreshes the badge too.
 */
export function useOverdueInvoiceCount(options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady, orgId } = useAuthContext();

  return useQuery({
    queryKey: [...invoiceKeys.lists(), 'overdueCount', orgId ?? null] as const,
    queryFn: () =>
      InvoicesService.getApiV1Invoices(
        'OVERDUE',
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        1,
        0,
      ),
    select: (data) => data.total_count,
    enabled: (options?.enabled ?? true) && isSignedIn && isAuthReady,
    staleTime: 60 * 1000,
    ...visiblePoll(NAV_BADGE_POLL_MS),
    retry: retryUnlessClientError,
  });
}
